// ANALYSIS AND CHANGES:
// 1. INLINE STYLES REMOVED: the `ConfigProvider` and the huge class string on the `Table` component were removed entirely. They were the cause of the style conflicts.
// 2. SIMPLIFICATION: the component is now purely functional. It renders a header with a search bar and the Ant Design table, with no design opinions of its own.
// 4. CLEAN STRUCTURE: the HTML/JSX was simplified to make external styling through `careersStyles.css` easier.

import { useState } from 'react';
import { ConfigProvider, Table, TableProps } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import enUS from 'antd/lib/locale/en_US';

interface GenericListProps<T> extends TableProps<T> {
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;
}

export function GenericList<T extends object>({
  onSearch,
  searchPlaceholder = "Search...",
  ...props
}: GenericListProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    if (onSearch) {
      onSearch('');
    }
  };

  return (
    // The table wrapper is now styled externally via CSS.
    <ConfigProvider locale={enUS}>
    <div className="ant-table-wrapper bg-zinc-900/50">
      <Table<T>
        {...props}
        // The table header (with the search) is defined here.
        title={() => (
          <div className="w-full md:w-1/3">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <SearchOutlined style={{ color: '#00ade6' }} />
              </div>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full sm:w-72 h-11 pl-11 pr-4 bg-zinc-800/80 border border-zinc-700 text-white placeholder-zinc-500 font-coda text-sm focus:outline-none focus:border-[#00ade6] focus:ring-1 focus:ring-[#00ade6]/50 transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-white transition-colors cursor-pointer"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        )}
        rowKey="id"
        locale={{
          emptyText: 'No data found',
          triggerDesc: 'Click to sort descending',
          triggerAsc: 'Click to sort ascending',
          cancelSort: 'Click to cancel sorting',
        }}
        pagination={{
          ...props.pagination,
          showTotal: (total, range) =>
            // The pagination text is now styled via careersStyles in TalentPoolPage.
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
    </div>
    </ConfigProvider>
  );
}
